import React, { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { googleAuthService } from '../../../services/google-auth-service';
import { yahooFantasyService } from '../../../services/yahoo-fantasy-service';
import { API_CONFIG, BASE_URL } from '../../../config';
import { TransformedMatchup } from '../../../../functions/src/server/data-mappers';
import { Button } from '../../shared/buttons';
import { AwardData } from '../../../../functions/src/server/types';

type MatchupResponse = {
    imageData: string;
    mimeType: string;
} & TransformedMatchup;

interface AwardsFormProps {
    week: number;
    matchups: MatchupResponse[] | undefined;
    selectedMatchupIndex: number;
}

export const AwardsForm: React.FC<AwardsFormProps> = ({ week, matchups, selectedMatchupIndex }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [blurb, setBlurb] = useState('');
    const [funFacts, setFunFacts] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
    const [isUploading, setIsUploading] = useState(false);
    const [isRefreshingHistory, setIsRefreshingHistory] = useState(false);
    const [refreshHistoryMessage, setRefreshHistoryMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // React Query for getting awards
    const { refetch: refetchAwards, isFetching: isFetchingAwards } = useQuery<AwardData>({
        queryKey: [
            'data',
            'awards',
            matchups?.[selectedMatchupIndex]
                ? { week, matchup: matchups[selectedMatchupIndex]?.id }
                : {},
        ],
        enabled: false, // Only fetch when manually triggered
    });

    // React Query mutation for creating an award
    const createAwardMutation = useMutation({
        // TODO type the data
        mutationFn: async awardData => {
            return yahooFantasyService.makeRequest(`${API_CONFIG.apiUri}/data/awards`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(awardData),
            });
        },
        onSuccess: () => {
            console.log('Award created successfully!');
        },
        onError: error => {
            console.error('Error creating award:', error);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!matchups || !matchups[selectedMatchupIndex]) {
            return;
        }

        const selected = matchups[selectedMatchupIndex];

        const awardData = {
            week,
            matchupId: selected.id,
            imageURL: uploadedImageUrl,
            team1: {
                name: selected.team1.name,
                logo: selected.team1.logo,
                points: selected.team1.points,
            },
            team2: {
                name: selected.team2.name,
                logo: selected.team2.logo,
                points: selected.team2.points,
            },
            title,
            description,
            blurb,
            funFacts,
        } as const;

        // @ts-expect-error server typing TBD
        createAwardMutation.mutate(awardData);
    };

    const getLeagueDebug = async () => {
        const leagueDebugData = await yahooFantasyService.makeRequest(
            `${API_CONFIG.apiUri}/league-debug`,
            {
                method: 'GET',
            }
        );
        console.log(leagueDebugData);
    };

    const refreshHistory = async () => {
        setIsRefreshingHistory(true);
        setRefreshHistoryMessage(null);
        try {
            const history = await yahooFantasyService.makeRequest(
                `${API_CONFIG.apiUri}/admin/refresh-history`,
                {
                    method: 'GET',
                }
            );
            console.log(history);
            const years = Array.isArray(history?.years) ? history.years.join(', ') : '';
            setRefreshHistoryMessage(
                years ? `History refreshed for ${years}` : 'History refreshed'
            );
        } catch (error) {
            console.error('Error refreshing history:', error);
            setRefreshHistoryMessage('Failed to refresh history');
        } finally {
            setIsRefreshingHistory(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClearForm = () => {
        setTitle('');
        setDescription('');
        setBlurb('');
        setFunFacts('');
        setSelectedFile(null);
        setImagePreview(null);
        setUploadedImageUrl('');
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleImageUpload = async () => {
        if (!selectedFile) {
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const idToken = await googleAuthService.getIdToken();

            // Note this isn't on the API_URL since it's not behind the regular /api prefix
            const response = await fetch(`${BASE_URL}uploadFile`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            setUploadedImageUrl(data.publicUrl);
        } catch (error) {
            console.error('Error uploading image:', error);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <div className="bg-slate-700 mb-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="title" className="block font-medium mb-2">
                            Title
                        </label>
                        <input
                            id="title"
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-600 text-white rounded border border-slate-500 focus:outline-none focus:border-blue-500"
                            placeholder="Enter award title"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="description" className="block font-medium mb-2">
                            Matchup Blurb
                        </label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-600 text-white rounded border border-slate-500 focus:outline-none focus:border-blue-500 min-h-[100px]"
                            placeholder="Enter award description"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="blurb" className="block font-medium mb-2">
                            Matchup Blurb (md allowed)
                        </label>
                        <textarea
                            id="blurb"
                            value={blurb}
                            onChange={e => setBlurb(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-600 text-white rounded border border-slate-500 focus:outline-none focus:border-blue-500 min-h-[100px]"
                            placeholder="Enter matchup blurb"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="funFacts" className="block font-medium mb-2">
                            Fun Facts (md allowed)
                        </label>
                        <textarea
                            id="funFacts"
                            value={funFacts}
                            onChange={e => setFunFacts(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-600 text-white rounded border border-slate-500 focus:outline-none focus:border-blue-500 min-h-[100px]"
                            placeholder="Enter fun facts"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="image" className="block font-medium mb-2">
                            Upload Image
                        </label>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                                <input
                                    id="image"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                    className="w-full px-3 py-2 bg-slate-600 text-white rounded border border-slate-500 focus:outline-none focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                                />
                                {imagePreview && (
                                    <>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="max-w-xs max-h-48 rounded border border-slate-500"
                                        />
                                    </>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <input
                                    id="imageurl"
                                    type="text"
                                    value={uploadedImageUrl}
                                    onChange={e => setUploadedImageUrl(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-600 text-white rounded border border-slate-500 focus:outline-none focus:border-blue-500"
                                    placeholder="Enter image url"
                                    required
                                />

                                <Button
                                    type="button"
                                    disabled={isUploading || !selectedFile}
                                    onClick={handleImageUpload}
                                >
                                    {isUploading ? 'Uploading...' : 'Upload Image'}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row gap-2">
                        <Button
                            type="submit"
                            disabled={createAwardMutation.isPending}
                            onClick={handleSubmit}
                        >
                            {createAwardMutation.isPending ? 'Creating...' : 'Create Award'}
                        </Button>
                        <Button
                            type="button"
                            onClick={async () => {
                                handleClearForm();
                                const updatedAwards = await refetchAwards();
                                setTitle(updatedAwards.data?.title || '');
                                setDescription(updatedAwards.data?.description || '');
                                setBlurb(updatedAwards.data?.blurb || '');
                                setFunFacts(updatedAwards.data?.funFacts || '');
                                setUploadedImageUrl(updatedAwards.data?.imageURL || '');
                            }}
                            disabled={
                                isFetchingAwards || !matchups || !matchups[selectedMatchupIndex]
                            }
                        >
                            {isFetchingAwards ? 'Loading...' : 'Get Awards'}
                        </Button>
                        <Button type="button" onClick={handleClearForm}>
                            Clear Form
                        </Button>
                        <Button type="button" onClick={getLeagueDebug}>
                            League Debug
                        </Button>
                        <Button
                            type="button"
                            onClick={refreshHistory}
                            disabled={isRefreshingHistory}
                        >
                            {isRefreshingHistory ? 'Refreshing...' : 'Refresh History'}
                        </Button>
                    </div>
                    {refreshHistoryMessage && (
                        <p
                            className={
                                refreshHistoryMessage.startsWith('Failed')
                                    ? 'text-red-400 text-sm'
                                    : 'text-green-400 text-sm'
                            }
                        >
                            {refreshHistoryMessage}
                        </p>
                    )}
                    {createAwardMutation.isSuccess && (
                        <p className="text-green-400 text-sm">Award created successfully!</p>
                    )}

                    {createAwardMutation.isError && (
                        <p className="text-red-400 text-sm">
                            Error: {createAwardMutation.error?.message}
                        </p>
                    )}
                </form>
            </div>
        </>
    );
};
